import { useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { useRouter } from "@/hooks/useRouter";
import { SearchChatModeContext } from "./SearchChatModeContext";
import type { Mode } from "./SearchChatModeContext";
import { useSearchChatCache } from "@/hooks/useSearchChatCache";

/**
 * Provider component that manages search/chat mode state
 * This ensures all components share the same mode state
 */
export function SearchChatModeProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = router.pathname;
  const { initialMode, saveToCache } = useSearchChatCache();

  // Initialize mode from localStorage or route
  const getInitialMode = (): Mode => {
    // Otherwise, check localStorage for user's last preference
    if (initialMode) {
      return initialMode;
    }

    // Default to chat
    return "chat";
  };

  const [mode, setModeState] = useState<Mode>(getInitialMode);

  // Update mode when route changes to search or chat pages
  useEffect(() => {
    if (pathname?.startsWith("/search")) {
      setModeState("search");
      if (typeof window !== "undefined") {
        saveToCache({ mode: "search" });
      }
    } else if (pathname?.startsWith("/chats")) {
      setModeState("chat");
      if (typeof window !== "undefined") {
        saveToCache({ mode: "chat" });
      }
    }
  }, [pathname, saveToCache]);

  // Persist mode changes to localStorage and update state
  const setMode = useCallback(
    (newMode: Mode) => {
      setModeState(newMode);
      if (typeof window !== "undefined") {
        saveToCache({ mode: newMode });

        // Dispatch a custom event for any legacy code that might be listening
        window.dispatchEvent(
          new CustomEvent("search-chat-mode-change", { detail: newMode })
        );
      }
    },
    [saveToCache]
  );

  return (
    <SearchChatModeContext.Provider value={{ mode, setMode }}>
      {children}
    </SearchChatModeContext.Provider>
  );
}
